import React from "react";
import PropTypes from "prop-types";
// Material imports
import DialogContent from "@mui/material/DialogContent";
import MuiDialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import { styled } from "@mui/material/styles";

// Limitless imports
import Grid from "@limitless/modern/layout/Grid";
import { Loading, ExpertConversation } from "@limitless/portal";
import Divider from "@limitless/modern/data-display/divider/Divider";
import { useExpertProfileContext } from "@limitless/expert";
import { BoxRenderer } from "@limitless/portal";
import Typography from "@limitless/modern/data-display/typography/Typography";
import { RatingSystem } from "customer/components";
import RatingMessageDetails, { renderRatingScore } from "./RatingMessageDetails";
import { useExpertRatingContext } from "@limitless/expert";
import Button from "@limitless/modern/inputs/button/Button";
import { commonRatingFilter } from "./Filters";
import IconButton from "@limitless/modern/inputs/icon-button/IconButton";
import CloseIcon from "@limitless/icons/Close";

const ButtonAction = styled(Button)(() => ({
    padding: 0,
    minWidth: "fit-content",
    ".MuiButton-textPrimary&:hover": {
        backgroundColor: "transparent!important"
    }
}));

const RatingMessage = ({
    messages,
    messageIndex,
    ratingMessageInfoOpen,
    setRatingMessageInfoOpen,
    isGenaiReviewMessage
}) => {
    const classes = commonRatingFilter();
    const [paginationIndex, setPaginationIndex] = React.useState(messageIndex);
    const { profile } = useExpertProfileContext();
    const { getRatingMessage, ratingMessage } = useExpertRatingContext();

    const isFirstIndex = React.useMemo(() => {
        return paginationIndex === 0;
    }, [paginationIndex]);

    const isLastIndex = React.useMemo(() => {
        return paginationIndex === messages.length - 1;
    }, [paginationIndex]);

    React.useEffect(() => {
        getRatingMessage(messages[paginationIndex].messageId, messages[paginationIndex]);
    }, [paginationIndex]);

    const handleRatingMessageInfoToggle = () => {
        setRatingMessageInfoOpen(!ratingMessageInfoOpen);
    };

    const buttonActionArray = React.useMemo(() => {
        return [
            {
                text: "Previous message",
                onClick: () => !isFirstIndex && setPaginationIndex(paginationIndex - 1),
                disabled: isFirstIndex,
                color: !isFirstIndex ? "primary.main" : "text.disable",
                icon: <KeyboardArrowLeft sx={{ width: "20px" }} />
            },
            {
                text: "Next message",
                onClick: () => !isLastIndex && setPaginationIndex(paginationIndex + 1),
                disabled: isLastIndex,
                color: !isLastIndex ? "primary.main" : "text.disable",
                icon: <KeyboardArrowRight sx={{ width: "20px" }} />,
                style: { flexDirection: "row-reverse" }
            }
        ];
    }, [paginationIndex]);

    return (
        <>
            <MuiDialog
                onClose={handleRatingMessageInfoToggle}
                aria-labelledby="rating-dialog-title"
                open={ratingMessageInfoOpen}
                PaperProps={{
                    elevation: 1,
                    sx: { padding: theme => theme.spacing(3), minWidth: "fit-content" }
                }}
            >
                <IconButton
                    icon={<CloseIcon sx={{ color: theme => theme.palette.primary.main }} />}
                    label={""}
                    onClick={handleRatingMessageInfoToggle}
                    sx={{
                        justifyContent: "end"
                    }}
                    id="iconButton"
                />
                <DialogContent>
                    {Object.keys(ratingMessage).length === 0 ? (
                        <Loading label="Loading rating message..." elevation={0} />
                    ) : (
                        <Grid container spacing={2}>
                            <Grid
                                item
                                xs={ratingMessage.reviewScore > 0 ? 6 : 12}
                                sx={{ height: "fit-content", width: "600px" }}
                            >
                                {ratingMessage.custRating > 0 && (
                                    <Grid>
                                        {ratingMessage.includedInRating &&
                                        ratingMessage.ratingType?.includes("customer") ? (
                                            <BoxRenderer
                                                fontSize={12}
                                                fontWeight={400}
                                                value={`Customer rating: ${ratingMessage.custRating}%`}
                                                color={
                                                    ratingMessage.custRating >= 80
                                                        ? "secondary-main"
                                                        : ratingMessage.custRating >= 50
                                                        ? "warning-light"
                                                        : "error-light"
                                                }
                                                variant="caption"
                                                showRadius
                                                noMargin
                                                addPadding
                                                centerTextAlign
                                                containerStyles={{ padding: "4px 8px" }}
                                            />
                                        ) : (
                                            renderRatingScore("Customer rating:", ratingMessage.custRating)
                                        )}

                                        <Typography variant="body2" mt={3}>
                                            Here you can see how the customer reviewed your conversation:
                                        </Typography>
                                        <Grid
                                            textAlign="center"
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="center"
                                            mt={2}
                                            mb={2}
                                        >
                                            <RatingSystem
                                                message={ratingMessage}
                                                moderniseStar
                                                disabled
                                                fontSize={26}
                                                useDefaultScore={false}
                                                compact
                                            />
                                            {ratingMessage.custTestimonial && (
                                                <Typography className={classes.italicTypography}>
                                                    {`" ${ratingMessage.custTestimonial} "`}
                                                </Typography>
                                            )}
                                        </Grid>
                                        <Divider className={classes.divider} />
                                    </Grid>
                                )}
                                <Typography variant="body2">{`Conversation with  ${ratingMessage.author}`}</Typography>
                                <div
                                    style={{
                                        overflowY: "auto",
                                        maxHeight: ratingMessage.custRating > 0 ? "340px" : "100%",
                                        margin: "15px 10px 0px 0px"
                                    }}
                                >
                                    <ExpertConversation
                                        profile={profile}
                                        message={ratingMessage}
                                        countryList={[]}
                                        scrollOnLoad={false}
                                        handleSuggestedAnswerSelection={() => null}
                                        suggestedAnswerState={""}
                                        isParentLoading={false}
                                    />
                                </div>
                            </Grid>
                            <Grid item xs={6}>
                                <RatingMessageDetails
                                    message={ratingMessage}
                                    isGenaiReviewMessage={isGenaiReviewMessage}
                                />
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <DialogContent>
                        <Grid pb={2}>
                            <Divider />
                        </Grid>
                        <Grid display="flex" flexDirection="row" justifyContent="space-between">
                            {buttonActionArray.map(action => {
                                return (
                                    <ButtonAction
                                        key={action.text}
                                        onClick={action.onClick}
                                        autoFocus
                                        disabled={action.disabled}
                                        style={action.style}
                                        variant={"text"}
                                    >
                                        {action.icon}
                                        <Typography color={action.color} variant="caption">
                                            {action.text}
                                        </Typography>
                                    </ButtonAction>
                                );
                            })}
                        </Grid>
                    </DialogContent>
                </DialogActions>
            </MuiDialog>
        </>
    );
};

RatingMessage.propTypes = {
    theme: PropTypes.object.isRequired,
    setRatingMessageInfoOpen: PropTypes.any,
    ratingMessageInfoOpen: PropTypes.any,
    messageIndex: PropTypes.number,
    messages: PropTypes.object,
    isGenaiReviewMessage: PropTypes.any
};

export default RatingMessage;
